import { i18n } from '@lingui/core';
import type { Messages } from '@lingui/core';
import { getDateFnsLocale as getFormatDateFnsLocale } from '@/lib/format';

// Available locales that match our lingui.config.js
export const AVAILABLE_LOCALES = ['en', 'de', 'fr', 'it', 'es', 'pt'] as const;
export type Locale = typeof AVAILABLE_LOCALES[number];

// Default locale
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Get the date-fns locale object for a Lingui locale, defaulting to the active one.
 * The map itself lives in `@/lib/format`.
 */
export function getDateFnsLocale(locale?: Locale) {
  return getFormatDateFnsLocale(locale || i18n.locale || DEFAULT_LOCALE);
}

// Static catalog imports using import.meta.glob for the compiled .mjs files
const CATALOGS = import.meta.glob('../locales/*/messages.mjs', { eager: false }) as Record<
  string,
  () => Promise<{ messages: Messages }>
>;

/**
 * Detect browser locale with fallback to default
 */
function detectBrowserLocale(): Locale {
  // Get browser language preference
  const browserLang = navigator.language.split('-')[0];
  
  // Check if browser language is in our available locales
  if (AVAILABLE_LOCALES.includes(browserLang as Locale)) {
    return browserLang as Locale;
  }
  
  // Fallback to default locale
  return DEFAULT_LOCALE;
}

/**
 * Load messages for a specific locale
 */
export async function loadLocale(locale: Locale) {
  try {
    // Get the catalog loader function from the static map
    const catalogPath = `../locales/${locale}/messages.mjs`;
    const catalogLoader = CATALOGS[catalogPath];
    
    if (!catalogLoader) {
      throw new Error(`Catalog for locale ${locale} not found`);
    }
    
    // Load the catalog
    const { messages } = await catalogLoader();
    
    // Load messages into i18n instance
    i18n.load(locale, messages);
    i18n.activate(locale);
    // Screen readers pick their voice and the browser its hyphenation from <html lang>.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    
    // Store locale preference in localStorage
    localStorage.setItem('lingui-locale', locale);
    
    return messages;
  } catch (error) {
    console.warn(`Failed to load locale ${locale}, falling back to ${DEFAULT_LOCALE}`, error);
    
    // If not default locale, try to load default
    if (locale !== DEFAULT_LOCALE) {
      return loadLocale(DEFAULT_LOCALE);
    }
    
    // If even default fails, we have a bigger problem
    throw new Error(`Failed to load default locale ${DEFAULT_LOCALE}`);
  }
}

/**
 * Get stored locale or detect browser locale
 */
export function getInitialLocale(): Locale {
  // Check localStorage first
  const storedLocale = localStorage.getItem('lingui-locale') as Locale;
  if (storedLocale && AVAILABLE_LOCALES.includes(storedLocale)) {
    return storedLocale;
  }
  
  // Otherwise detect from browser
  return detectBrowserLocale();
}

/**
 * Initialize i18n with detected/stored locale
 */
export async function initializeI18n(): Promise<Locale> {
  const locale = getInitialLocale();
  await loadLocale(locale);
  return locale;
}

// Export the configured i18n instance
export { i18n }; 